import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchArtistInterviews, fetchArtistInterviewsWithFilters, fetchArtistInterviewById, createOrUpdateArtistInterview } from '../services/api';

// Fetch all artist interviews (old endpoint - kept for backward compatibility)
export const getArtistInterviews = createAsyncThunk(
    'artistInterviews/getArtistInterviews',
    async () => {
        const response = await fetchArtistInterviews();
        return response.data;
    }
);

// Fetch artist interviews with filters and pagination
export const getArtistInterviewsWithFilters = createAsyncThunk(
    'artistInterviews/getArtistInterviewsWithFilters',
    async (params, { rejectWithValue }) => {
        try {
            const data = await fetchArtistInterviewsWithFilters(params);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch artist interviews.');
        }
    }
);

// Fetch a single artist interview by ID
export const getArtistInterviewById = createAsyncThunk(
    'artistInterviews/getArtistInterviewById',
    async (id) => {
        const response = await fetchArtistInterviewById(id);
        return response.data;
    }
);

// Create or update an artist interview
export const saveArtistInterview = createAsyncThunk(
    'artistInterviews/saveArtistInterview',
    async (interviewData, { rejectWithValue }) => {
        try {
            const response = await createOrUpdateArtistInterview(interviewData);
            return response.data;
        } catch (error) {
            // Return the error response data for better error handling
            return rejectWithValue(error.response?.data || { message: 'Failed to save interview' });
        }
    }
);

const artistInterviewsSlice = createSlice({
    name: 'artistInterviews',
    initialState: {
        artistInterviews: [],
        selectedInterview: null,
        status: 'idle',
        error: null,
        pagination: {
            page: 1,
            page_size: 20,
            count: 0,
            total_count: 0,
            total_pages: 1,
        },
        filters: {},
    },
    reducers: {
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        resetFilters: (state) => {
            state.filters = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getArtistInterviews.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getArtistInterviews.fulfilled, (state, action) => {
                state.artistInterviews = action.payload.data;
                state.status = 'succeeded';
            })
            .addCase(getArtistInterviews.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getArtistInterviewsWithFilters.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getArtistInterviewsWithFilters.fulfilled, (state, action) => {
                state.artistInterviews = action.payload.data || [];
                if (action.payload.pagination) {
                    state.pagination = {
                        page: action.payload.pagination.page || 1,
                        page_size: action.payload.pagination.page_size || 20,
                        count: action.payload.pagination.count || 0,
                        total_count: action.payload.pagination.total_count || 0,
                        total_pages: action.payload.pagination.total_pages || 1,
                    };
                }
                state.status = 'succeeded';
            })
            .addCase(getArtistInterviewsWithFilters.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || action.error.message;
            })
            .addCase(getArtistInterviewById.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getArtistInterviewById.fulfilled, (state, action) => {
                state.selectedInterview = action.payload.data;
                state.status = 'succeeded';
            })
            .addCase(getArtistInterviewById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(saveArtistInterview.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(saveArtistInterview.fulfilled, (state, action) => {
                state.status = 'succeeded';
            })
            .addCase(saveArtistInterview.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { setFilters, resetFilters } = artistInterviewsSlice.actions;

export default artistInterviewsSlice.reducer;
